import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { display, flexbox, styled } from '@mui/system';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { addDays, isSameDay } from 'date-fns';
import Slot from './components/Slot';
import useGetAvailabilityLazy from '@src/adapters/hooks/availability/useGetAvailabilities';
import { enqueueSnackbar, useSnackbar } from 'notistack';
import useAuthKeyStore from '@src/stores/useAuthKeyStore';
import { ReactComponent as Logo } from '@src/assets/logo.svg';
import {
  AvailabilityState,
  AvailabilityStateType,
} from '@src/constants/AvailabilityState';
import useSaveAvailabilityLazy from '@src/adapters/hooks/availability/useSaveAvailability';
import dayjs, { Dayjs } from 'dayjs';
import { JobBookingStatus } from '@src/constants/JobBookingStatus';
import utc from 'dayjs/plugin/utc';
import JobPreview from './components/JobPreview';
import ErrorImage from '@src/assets/images/keyerror.svg';
import ErrorBackground from '@src/assets/images/keyerrorbackground.svg';
import UserAvailabilityDTO from '@src/adapters/dto/user/UserAvailabilityDTO';
import { useResendUserLinkLazy } from '@src/adapters/hooks/user/useResendUserLink';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Controller } from 'react-hook-form';
import useSaveAvailabilityNotificationDelayLazy from '@src/adapters/hooks/availability/useSaveNotificationDelay';
import useResetUserReminderDelayLazy from '@src/adapters/hooks/user/useResetUserReminderDelay';
dayjs.extend(utc);

const PREFIX = 'availability';

const classes = {
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  footer: `${PREFIX}-footer`,
  logo: `${PREFIX}-logo`,
  delayUntil: `${PREFIX}-delayUntil`,
};

const MainContainer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.main,
  [`& .${classes.title}`]: {
    textAlign: 'center',
    color: theme.palette.text.primary,
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  [`& .${classes.subtitle}`]: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  [`& .${classes.footer}`]: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(7),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  [`& .${classes.logo}`]: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
  },
  [`& .${classes.delayUntil}`]: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
}));

const StyledDates = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  marginTop: theme.spacing(4),

  [`& .${classes.title}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  [`& .${classes.subtitle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}));

const editableAvailabilities: AvailabilityStateType[] = [
  'NotSet',
  'Available',
  'NotAvailable',
];

function Availability() {
  const location = useLocation();
  const { setGCKey, gcKey } = useAuthKeyStore();

  const { execute: getAvailabilites } = useGetAvailabilityLazy();
  const { execute: saveAvailability } = useSaveAvailabilityLazy();
  const { execute: saveDelay } = useSaveAvailabilityNotificationDelayLazy();
  const { execute: resendLink } = useResendUserLinkLazy();
  const { execute: resetReminders } = useResetUserReminderDelayLazy();

  const [availabilities, setAvailabilities] = useState<UserAvailabilityDTO[]>(
    []
  );
  const [showDetails, setShowDetails] = useState(false);
  const [selected, setSelected] = useState<UserAvailabilityDTO>();
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [isKeyError, setIsKeyError] = useState(false);
  const [date, setDate] = useState<Dayjs | null>(null);

  const hasFetchedRef = useRef(false);
  const userChangedDate = useRef(false);

  useEffect(() => {
    if (!userChangedDate.current) {
      return;
    }

    if (date !== null) {
      (async () => {
        try {
          await saveDelay({
            date: dayjs(date).toDate(),
          });
        } catch (e) {
          console.error('Error saving delay:', e);
        } finally {
          userChangedDate.current = false;
        }
      })();
    }
  }, [date]);

  useEffect(() => {
    const key = location.hash.substring(1);

    if (!key) {
      setIsLoading(false);
      setIsValid(false);
    } else {
      setGCKey(key);
    }
  }, [location.hash, setGCKey]);

  useEffect(() => {
    const hasFetched = hasFetchedRef.current;
    if (hasFetched) {
      return;
    }
    hasFetchedRef.current = true;

    const init = async () => {
      try {
        const apiResult = await getAvailabilites();
        const today = dayjs().utc().startOf('day').toDate();

        const dates = Array.from({ length: 28 }, (_, i) => addDays(today, i));

        const baseAvailabilities: UserAvailabilityDTO[] = dates.map((d) => ({
          id: undefined,
          date: d.toString(),
          state: AvailabilityState.NotSet,
          jobStatus: undefined,
          jobId: undefined,
        }));

        setDate(
          apiResult.delayUntilDate ? dayjs(apiResult.delayUntilDate) : null
        );

        const merged = baseAvailabilities.map((local) => {
          const match = apiResult.availabilities.find((av) =>
            isSameDay(new Date(av.date), new Date(local.date))
          );
          return match ? { ...local, ...match } : local;
        });

        setAvailabilities(merged);
        setIsValid(true);
      } catch (e: any) {
        if (e && e.status === 403) {
          setIsKeyError(true);
        }
        setIsValid(false);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [getAvailabilites]);

  const onAvailabilityPressed =
    (availability: UserAvailabilityDTO) => async () => {
      if (
        availability.jobStatus &&
        availability.jobId &&
        [
          JobBookingStatus.Booked,
          JobBookingStatus.Pencilled,
          JobBookingStatus.PencilAccepted,
          JobBookingStatus.Confirmed,
        ].includes(availability.jobStatus)
      ) {
        setSelected(availability);
        setShowDetails(true);
        return;
      }

      if (editableAvailabilities.includes(availability.state)) {
        // Can be edited
        const nextStateIndex =
          (editableAvailabilities.indexOf(availability.state) + 1) %
          editableAvailabilities.length;

        const nextState = editableAvailabilities[nextStateIndex];

        setAvailabilities((prevValue) => {
          const other = prevValue.filter((pv) => pv !== availability);
          if (availability) {
            availability.state = nextState;
            return [availability, ...other];
          }
          return other;
        });
        try {
          await saveAvailability({
            date: dayjs(availability.date).toDate(),
            state: nextState,
          });
        } catch (e) {}
      }
    };

  const sortedAvailability = useMemo(() => {
    return availabilities.sort(
      (a, b) =>
        dayjs(a.date).toDate().getTime() - dayjs(b.date).toDate().getTime()
    );
  }, [availabilities]);

  const handleClose = (status?: JobBookingStatus) => {
    if (status && selected) {
      setAvailabilities((prev) => {
        return prev.map((p) =>
          p.id === selected.id
            ? {
                ...p,
                jobStatus:
                  status === JobBookingStatus.Unassigned ? undefined : status,
                state:
                  status === JobBookingStatus.Unassigned
                    ? AvailabilityState.Available
                    : p.state,
              }
            : p
        );
      });
    }

    setSelected(undefined);
    setShowDetails(false);
  };

  const handleResend = async () => {
    if (gcKey) {
      await resendLink({ key: gcKey });

      enqueueSnackbar('New link has been sent', {
        variant: 'success',
      });
    }
  };

  const handleReset = async () => {
    if (gcKey) {
      await resetReminders({ key: gcKey });
      setDate(null);
    }
  };

  return (
    <MainContainer>
      <Box className={classes.logo}>
        <Logo title="logo" />
      </Box>
      {!isLoading && (
        <>
          {isValid && (
            <>
              <Typography variant="h5" className={classes.title}>
                Set availability
              </Typography>
              <Box className={classes.delayUntil}>
                <Typography variant="body1" className={classes.subtitle}>
                  {date
                    ? `Remiders delayed until ${dayjs(date).format(
                        'DD/MM/YYYY'
                      )}`
                    : 'Delay reminders until'}
                </Typography>
                {!date && (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="en-gb">
                    <DatePicker
                      value={date}
                      onChange={(newValue) => {
                        userChangedDate.current = true;
                        setDate(newValue);
                      }}
                      slots={{
                        textField: TextField,
                      }}
                      slotProps={{
                        textField: {
                          variant: 'outlined',
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
                {date && (
                  <Button variant="outlined" size="small" onClick={handleReset}>
                    Restart Reminders (SMS)
                  </Button>
                )}
              </Box>
              <Typography variant="body1" className={classes.subtitle}>
                Tap the status of a day to update availability.
              </Typography>
              <StyledDates>
                {gcKey &&
                  sortedAvailability.map((availability, index) => (
                    <Slot
                      key={index}
                      availability={availability}
                      onPressed={onAvailabilityPressed(availability)}
                    />
                  ))}
              </StyledDates>
              <Typography variant="body1" className={classes.footer}>
                Once finished, you can close your browser.
              </Typography>
              {selected && (
                <JobPreview
                  open={showDetails}
                  availability={selected}
                  close={handleClose}
                />
              )}
            </>
          )}
          {!isValid && (
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center">
              <Grid
                size={12}
                sx={{
                  width: 250,
                  height: 250,
                  backgroundImage: `url(${ErrorBackground})`,
                  backgroundRepeat: 'no-repeat',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '30px',
                }}>
                <img src={ErrorImage} alt="ErrorImage" />
              </Grid>
              <Grid size={12}>
                <Typography variant="h5" align="center">
                  That link doesn’t seem to work
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="body1" align="center">
                  It looks like the {isKeyError ? 'key' : 'link'} you used has
                  expired or is no longer valid. Please contact support if you
                  need help with this issue.
                </Typography>
              </Grid>
              <Grid
                size={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                {gcKey && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleResend}>
                    Re-Send Link
                  </Button>
                )}
              </Grid>
            </Grid>
          )}
        </>
      )}
    </MainContainer>
  );
}

export default Availability;
